import {
    ALWAYS_GRANTED,
    permissionPolicyFor,
    ROLE_GRANTS,
    RoleGrantKey,
    RoleGrantSelection,
} from './rolePolicyBuilder';

/**
 * FNP-1365 — ASK FOR A DESTRUCTIVE PERMISSION WHEN IT IS NEEDED, NOT AT CONNECT.
 *
 * FNP-1363 shipped the other half of this idea: nothing destructive is
 * pre-ticked on the connect drawer, because a pre-ticked "delete my hosted
 * zones" is exactly the box that turns a hesitant yes into a no, and losing the
 * connection loses everything rather than just the delete.
 *
 * That posture is only honest if the permission is asked for LATER, at the
 * moment it is actually needed. Otherwise "default it off" is just "remove the
 * feature". This file is that later moment.
 *
 * ⚠️ WHY ASKING LATER IS A BETTER ASK, AND NOT MERELY A DEFERRED ONE.
 * At connect time the customer is weighing a blanket grant for a hypothetical
 * future, from a company they may barely know, before they have seen anything
 * work. Here they have used the product, they are looking at one specific
 * thing they are trying to delete, and saying no costs them that one action
 * instead of the whole integration. Every part of the frame is better.
 *
 * ⚠️ THE INCREMENTAL POLICY IS THE WHOLE POLICY, NOT A FRAGMENT.
 *
 * The natural-seeming design is to hand over just the missing statement —
 * "add `acm:DeleteCertificate`". It is wrong, and quietly so: an IAM inline
 * policy is REPLACED by what you paste, never merged. A customer pasting a
 * one-action fragment over their existing policy would revoke every grant they
 * already made and break the connection they came here with. So
 * `elevatedPolicyFor` returns current-plus-one, complete and pasteable, and the
 * copy says it replaces.
 *
 * ⚠️ ONE SOURCE FOR THE ACTION LIST, STILL. Every action name here comes from
 * `ROLE_GRANTS` via `permissionPolicyFor`. FNP-1400 finished removing the
 * second hand-maintained copy of these lists; re-typing `acm:DeleteCertificate`
 * in an elevation prompt would put it straight back, and the copy that drifts
 * is the one a customer pastes.
 */

/**
 * The grants this feature will ask for after the fact.
 *
 * ⚠️ DESTRUCTIVE ONLY, AND `writeRecords` IS DELIBERATELY NOT HERE.
 *
 * The ticket's title is the scope: destructive permissions. Three reasons to
 * hold that line rather than generalise the mechanism to every grant:
 *
 *   · Changing records is what most tenants CONNECT FOR. It is pre-ticked on
 *     the connect drawer precisely because withholding it leaves a connection
 *     that does almost nothing, so a tenant reaching a record-write prompt has
 *     made a deliberate read-only choice — interrupting that with an upsell is
 *     the disruption the ticket's own open question warns about.
 *   · Record writes happen constantly. A destructive action happens rarely and
 *     is deliberate, which is what makes an interruption proportionate there
 *     and nagging here.
 *   · `readDns` cannot be withheld at all (see `ALWAYS_GRANTED`), and create
 *     grants degrade gracefully — the customer makes the zone in AWS first.
 *
 * Derived from the definitions rather than listed, so a future destructive
 * grant is covered the day it is added and cannot be forgotten here.
 */
export const ELEVATABLE_GRANTS: readonly RoleGrantKey[] = ROLE_GRANTS
    .filter(grant => grant.destructive === true)
    .map(grant => grant.key);

export const isElevatable = (grant: RoleGrantKey): boolean =>
    ELEVATABLE_GRANTS.indexOf(grant) !== -1;

/**
 * The definition behind a grant — its label, its actions, and what withholding
 * it costs. The prompt renders the customer's own words back at them.
 *
 * ⚠️ LOOKED UP, NEVER RESTATED. The elevation prompt says the same sentence
 * about deleting hosted zones that the connect drawer said, because it is the
 * same sentence: `withholdingCost` is exactly "what you gave up", which is what
 * a customer meeting this prompt is now running into.
 */
export const grantDefinition = (grant: RoleGrantKey) =>
    ROLE_GRANTS.find(definition => definition.key === grant);

/** The literal IAM actions a single grant adds, e.g. `['acm:DeleteCertificate']`. */
export const actionsAdded = (grant: RoleGrantKey): readonly string[] =>
    grantDefinition(grant)?.actions ?? [];

/**
 * The policy the customer should now hold: everything they have today, plus
 * this one grant.
 *
 * ⚠️ NOT A DIFF, AND THAT IS A SAFETY PROPERTY RATHER THAN A STYLE CHOICE. See
 * the file header: pasting a fragment over an inline policy revokes everything
 * omitted. The customer is told to replace, and what they are given is
 * complete, so replacing is correct.
 *
 * ⚠️ `readDns` RIDES ALONG WHETHER OR NOT IT IS IN THE SELECTION, because
 * `permissionPolicyFor` always includes `ALWAYS_GRANTED`. A role that cannot
 * list zones cannot validate, so a policy without it is one that breaks the
 * connection — this function cannot emit that even if handed a selection
 * claiming otherwise.
 */
export const elevatedPolicyFor = (
    current: RoleGrantSelection,
    grant: RoleGrantKey,
): string => permissionPolicyFor({...current, [grant]: true});

/**
 * The selection a connection's stored declaration amounts to, plus the one
 * grant being asked for — what the re-declaration should say.
 *
 * ⚠️ SEEDED FROM WHAT IS STORED, NEVER FROM AN EMPTY SELECTION. The same rule
 * `DeclareCapabilitiesDrawer` follows for the same reason: a correction states
 * the COMPLETE intended state, so anything absent from the seed is submitted as
 * a revocation of a grant the customer still holds. Granting delete must not
 * cost them their record writes.
 */
export const elevatedSelection = (
    current: RoleGrantSelection,
    grant: RoleGrantKey,
): RoleGrantSelection => ({...current, [grant]: true, [ALWAYS_GRANTED]: true});

/**
 * The `certificate` block for a declaration, built ONLY from values that were
 * actually read.
 *
 * ⚠️ WHY THIS IS NOT `buildDeclareInput`'S JOB, AND WHY IT LIVES HERE.
 *
 * `buildDeclareInput` builds its block from a whole `RoleGrantSelection` and
 * sends it only when its caller passes `certificateKnown` (FNP-1398). That fits
 * the correction drawer, which edits every grant at once and has a connection
 * in hand to read readability from.
 *
 * The certificates module is in a different position: its own service reads
 * `certificateCapabilities` off the connection list and exposes both flags
 * truthfully through `useCertificateWriteCapabilities`. So at a certificate
 * delete we DO know the current values, and can state them.
 *
 * ⚠️ WHICH MEANS THE CALLER MUST PASS WHAT IT READ, NEVER A DEFAULT. Both
 * arguments are required and unpromoted booleans on purpose: an optional
 * parameter here would let a caller omit `canRequest` and silently revoke the
 * tenant's ability to issue certificates as a side effect of granting delete.
 * A correction states the complete intended state, so every field must be one
 * the caller actually knows.
 */
export const certificateDeclaration = (
    canRequest: boolean,
    canDelete: boolean,
): {canRequest: boolean, canDelete: boolean} => ({canRequest, canDelete});
